import { amongMultipleTranslatedOperatorsSpecs, dateOperatorsSpecs, IFilter, IFilterFieldSpec, stringOperatorsSpecs } from '@aston/foundation/models';
import { DunningLevel, IFilterOperator, PortalRequestStatus } from '@aston/foundation/enums';
import { notNone, sortFilters } from '@aston/foundation/functions';
import { TranslateService } from '@ngx-translate/core';
import { inject, Injectable } from '@angular/core';
import { map, of } from 'rxjs';

const statuses = Object.keys(PortalRequestStatus).filter(notNone).reduce((acc, s) => ({ ...acc, [`PortalRequests.Status.${s}`]: s }), {})
const types = [DunningLevel.PAA, DunningLevel.PPA, DunningLevel.LTG].reduce((acc, s) => ({ ...acc, [`PortalRequests.Modal.Type.${s}`]: s }), {})

@Injectable({ providedIn: 'root' })
export class SuperDebtorsRequestsListFilterSpecService {

	translateService = inject(TranslateService)

	completeStatuses = (term: string) => this.translateService.stream(Object.keys(statuses)).pipe(
		map(values => Object.entries(values).map(([value, label]: [string, string]) => ({ value: statuses[value], label }))),
		map(values => values.filter(t => !term || t.label.toLowerCase().includes(term.toLowerCase())))
	)

	completeTypes = (term: string) => this.translateService.stream(Object.keys(types)).pipe(
		map(values => Object.entries(values).map(([value, label]: [string, string]) => ({ value: types[value], label }))),
		map(values => values.filter(t => !term || t.label.toLowerCase().includes(term.toLowerCase())))
	)

	requestidFilterSpec: IFilterFieldSpec = {
		field: {label: 'DataList.Columns.Id', value: 'PortalRequestId'},
		operatorSpecs: [
			{ operator: IFilterOperator.Equals },
			{ operator: IFilterOperator.Contains },
			{ operator: IFilterOperator.NotContains }
		],
	}

	requestFunctionalIdFilterSpec: IFilterFieldSpec = {
		field: {label: 'DataList.Columns.Id', value: 'PortalRequestFunctionalId'},
		operatorSpecs: [
			{ operator: IFilterOperator.Equals },
			{ operator: IFilterOperator.Contains },
			{ operator: IFilterOperator.NotContains }
		],
	}

	commentFilterSpec = {
		field: {label: 'DataList.Columns.Comment', value: 'RequestCommentary'},
		operatorSpecs: stringOperatorsSpecs,
		defaultOperator: IFilterOperator.Contains,
	}

	statusFilterSpec: IFilterFieldSpec = {
		field: {label: 'DataList.Columns.Status', value: 'PortalRequestStatus'},
		operatorSpecs: amongMultipleTranslatedOperatorsSpecs,
		defaultOperator: IFilterOperator.Among,
		autocompleter: this.completeStatuses,
		chipsOptions: {
			minimumTextLength: 0,
			showDropdownIfEmpty: true,
		}
	}

	answerDateFilterSpec: IFilterFieldSpec = {
		field: {label: 'DataList.Columns.AnswerDate', value: 'AnswerDate'},
		operatorSpecs: dateOperatorsSpecs,
		isDate: true,
		isDateOnly: true,
		hasDateExpression: true
	}

	requestDateFilterSpec: IFilterFieldSpec = {
		field: {label: 'DataList.Columns.RequestDate', value: 'RequestDate'},
		operatorSpecs: dateOperatorsSpecs,
		isDate: true,
		isDateOnly: true,
		hasDateExpression: true
	}

	requestTypeFilterSpec: IFilterFieldSpec = {
		field: {label: 'PortalRequests.Modal.Type.Label', value: 'DunningLevel'},
		operatorSpecs: amongMultipleTranslatedOperatorsSpecs,
		defaultOperator: IFilterOperator.Among,
		autocompleter: this.completeTypes,
		chipsOptions: {
			minimumTextLength: 0,
			showDropdownIfEmpty: true,
		}
	}

	spec$ = of({
		items: [
			this.requestFunctionalIdFilterSpec,
			this.requestTypeFilterSpec,
			this.statusFilterSpec,
			this.commentFilterSpec,
			this.answerDateFilterSpec,
			this.requestDateFilterSpec,
		]
			.filter(Boolean)
			.sort(sortFilters(this.translateService))
	})

	byId(operator = IFilterOperator.Equals, ...values: number[]): IFilter {
		return {
			field: this.requestidFilterSpec.field.value,
			spec: this.requestidFilterSpec,
			values,
			operator,
		}
	}

	byStatuses(operator = IFilterOperator.Equals, ...values: PortalRequestStatus[]): IFilter {
		return {
			field: this.statusFilterSpec.field.value,
			spec: this.statusFilterSpec,
			values,
			operator,
		}
	}
}